import styles from "./styles.module.scss";

export const WhatIsNyleMaterial: React.FC = () => {
  return (
    <div className={styles.material}>
      <p>
        Nyle is the world's first AI-powered Operating System for eCommerce
        Growth – but what does that actually mean for your business?
      </p>
      <p>Think of traditional tools as single-purpose gadgets:</p>
      <ul>
        <li>Your analytics tool shows you data</li>
        <li>Your keyword tool finds keywords</li>
        <li>Your inventory tool tracks stock</li>
      </ul>
      <p>Nyle is different. It's your complete business command center that:</p>
      <ul>
        <li>🧠 Thinks strategically about your entire operation</li>
        <li>🔍 Identifies opportunities you'd never spot manually</li>
        <li>⚡ Takes action automatically to improve performance</li>
        <li>📈 Learns and adapts based on what works for your business</li>
      </ul>
      <p>
        Instead of juggling multiple tools and making educated guesses, you get
        one intelligent system that manages your growth while you focus on
        building your brand.
      </p>
    </div>
  );
};

export const ConnectAmazonAccountsMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>
        Unlock the Full Power of Data-Driven Insights for Your Amazon Business
      </h4>
      <p>
        <strong>Why this step is crucial: </strong> Without access to your
        business data, even the most advanced AI is working blind. This
        connection is what transforms Nyle from a generic tool into your
        personalized growth engine.
      </p>
      <p>Here's exactly what connecting your accounts does:</p>
      <h4>🔗 Amazon Seller Account Connection:</h4>
      <ul>
        <li>Sales Performance: Orders, revenue, units sold, and trends</li>
        <li>
          Inventory Data: Stock levels, fulfillment metrics, and turnover rates
        </li>
        <li>
          Customer Insights: Search behavior, buying patterns, and preferences
        </li>
        <li>
          Competitive Intelligence: How you stack up against others in your
          category
        </li>
      </ul>

      <h4>🎯 Amazon Ads Account Connection:</h4>
      <ul>
        <li>
          Campaign Performance: Clicks, impressions, conversions, and spend
        </li>
        <li>Keyword Intelligence: Which terms drive sales vs. just traffic</li>
        <li>ACOS & ROAS Tracking: Real profitability of your advertising</li>
        <li>
          Optimization Opportunities: Where you're leaving money on the table
        </li>
      </ul>
      <p>
        <strong>What happens immediately after connection:</strong> Our advanced
        AI and machine learning models start processing your data to create a
        comprehensive picture of your business. Within minutes, you'll see
        actionable recommendations tailored specifically to your products,
        market, and goals.
      </p>
    </div>
  );
};

export const AddCogsMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>Transform Revenue Numbers into Real Profitability Insights</h4>

      <p>
        <strong>The challenge:</strong> Most sellers track revenue and think
        they're profitable, but without knowing your true costs, you might be
        losing money on every sale.
      </p>

      <p>
        <strong>Why COGS (Cost of Goods Sold) is critical:</strong> Currently,
        your profitability metrics are blurred in our interface because we don't
        know your costs. Once you add COGS, everything becomes crystal clear.
      </p>

      <p>What you need to enter:</p>

      <h4>🏭 Cost of Goods Sold (COGS):</h4>
      <ul>
        <li>The direct cost to produce or purchase each product</li>
        <li>Include materials, manufacturing, and supplier costs</li>
        <li>Be product-specific – each ASIN should have its own COGS</li>
      </ul>

      <h4>📦 Miscellaneous Costs:</h4>
      <ul>
        <li>Shipping from suppliers to your warehouse</li>
        <li>Import duties and customs fees</li>
        <li>Quality control and inspection costs</li>
        <li>Any other costs directly tied to getting products ready to sell</li>
      </ul>

      <h4>What you'll instantly see once COGS is entered:</h4>
      <ul>
        <li>
          ✅ <strong>ROI per ASIN:</strong> Discover which products actually
          make you money (you might be surprised!)
        </li>
        <li>
          ✅ <strong>Contribution Profit per ASIN:</strong> Understand exactly
          how much each product contributes to your bottom line
        </li>
        <li>
          ✅ <strong>True Profitability Rankings:</strong> See which products
          deserve more investment vs. which ones to phase out
        </li>
      </ul>

      <p>
        <strong>Pro tip:</strong> Accurate COGS data is the foundation of every
        smart business decision. Take time to get this right – your future self
        will thank you.
      </p>
    </div>
  );
};

export const MarketIntelligenceMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>Stop Flying Blind – See Your Real Position in the Market</h4>

      <p>
        <strong>The problem most sellers face:</strong> They focus narrowly on
        ACOS targets without understanding the bigger picture. This leads to
        missed opportunities, inefficient spending, and suboptimal growth.
      </p>

      <p>
        <strong>What Market Intelligence reveals:</strong> Market Intelligence
        gives you data-driven insights about your competitors, sales velocity
        (both ad sales and organic sales), and market trends. It's like having a
        team of analysts constantly monitoring your industry.
      </p>
    </div>
  );
};

export const CheckMarketIntelligenceMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>Elevate Your Business Strategy with Market Intelligence</h4>

      <p>Instead of optimizing in isolation, you'll understand:</p>

      <h4>🎯 Your True Market Position:</h4>
      <ul>
        <li>Exactly where you rank against competitors</li>
        <li>Your current market share vs. your potential</li>
        <li>Which competitors are gaining or losing ground</li>
      </ul>

      <h4>📊 Sales Velocity Intelligence:</h4>
      <ul>
        <li>How fast products are selling in your category</li>
        <li>Seasonal trends and demand patterns</li>
        <li>Emerging opportunities before competitors notice them</li>
      </ul>

      <h4>💡 Strategic Insights You'll Discover:</h4>
      <ul>
        <li>Underserved niches in your market</li>
        <li>Pricing opportunities based on competitor gaps</li>
        <li>Product features that customers want but aren't getting</li>
        <li>The "big picture" context that transforms your decision-making</li>
      </ul>

      <p>
        <strong>Why this matters:</strong> When you understand your market
        deeply, every optimization decision becomes smarter and more profitable.
      </p>
    </div>
  );
};

export const WhatIfScenarioMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>Try "What If" Scenarios</h4>

      <p>
        <strong>Explore Different Futures Before Committing Your Budget</strong>
      </p>

      <p>
        The "What If" mode is your crystal ball for business planning. Instead
        of making changes and hoping for the best, you can model different
        scenarios and see the likely outcomes.
      </p>

      <p>
        <strong>How it works:</strong>
      </p>
      <ul>
        <li>Sales performance across all channels</li>
        <li>Required advertising investment</li>
        <li>Profitability and contribution margins</li>
        <li>Overall business metrics</li>
      </ul>

      <p>
        <strong>Smart Ways to Use "What If" Scenarios:</strong>
      </p>
      <ul>
        <li>
          🔬 <strong>Start Small:</strong> Begin with minor adjustments (like
          +1% market share) to understand immediate effects
        </li>
        <li>
          📊 <strong>Combine Metrics:</strong> Look at multiple metrics
          simultaneously – never optimize just one number in isolation
        </li>
        <li>
          💾 <strong>Save Scenarios:</strong> Bookmark your "what if" scenarios
          for future reference and comparison
        </li>
        <li>
          🤝 <strong>Collaborate:</strong> Share findings with your team to
          align everyone on growth strategies
        </li>
      </ul>

      <p>
        <strong>Example in action:</strong> "What if we increased our market
        share from 3% to 5% over the next quarter? How much additional ad spend
        would that require, and what would be our expected ROI?"
      </p>
    </div>
  );
};

export const SetupMarketShareMaterial = () => {
  return (
    <div className={styles.material}>
      <h4>Turn Insights into Action with Strategic Goal Setting</h4>
      <p>
        <strong>The power of the "What If" mode:</strong> This isn't just
        planning – it's strategic forecasting that helps you set realistic
        targets and achieve sustainable growth, all backed by real market data.
      </p>

      <h4>🎯 Option 1: Recommended Goal</h4>
      <ul>
        <li>
          Our platform analyzes your current performance, market trends, and
          industry benchmarks
        </li>
        <li>We suggest a realistic target that's ambitious but achievable</li>
        <li>
          Perfect if you're unsure where to start or want data-driven guidance
        </li>
      </ul>

      <h4>️⚙ Option 2: Custom Goal</h4>
      <ul>
        <li>Set your own Market Share Goal for a specific time period</li>
        <li>Choose your timeline (next quarter, next year, etc.)</li>
        <li>Full control over your planning and expectations</li>
      </ul>

      <h4>What happens after you set your goal:</h4>
      <ul>
        <li>
          <strong>💾 We Save Your Goal:</strong> Your target becomes the
          foundation for all our AI recommendations
        </li>
        <li>
          <strong>📋 You Get an Action Plan:</strong> We provide specific,
          prioritized steps to achieve your goal
        </li>
        <li>
          <strong>📈 Ongoing Optimization:</strong> Our AI continuously tracks
          your progress and adjusts recommendations
        </li>
      </ul>

      <h4>🎯 Why Set a Market Share Goal?</h4>
      <ul>
        <li>
          <strong>✅ Gain Clarity:</strong> Transform vague growth ambitions
          into measurable targets
        </li>
        <li>
          <strong>✅ Stay Competitive:</strong> Align your strategy with market
          realities and opportunities
        </li>
        <li>
          <strong>✅ Make Smarter Decisions:</strong> Every choice becomes
          data-backed rather than gut-feeling
        </li>
        <li>
          <strong>✅ Track Progress:</strong> Know exactly whether you're on
          track to hit your targets
        </li>
      </ul>

      <p>
        <strong>The bottom line:</strong> Whether you choose our recommended
        goal or create your own, our platform supports you every step of the way
        with actionable insights and strategic guidance.
      </p>
    </div>
  );
};
